"use client";

import Link from "next/link";
import { useEffect, useRef } from "react";

const Tabs = ({
	items = [],
	value,
	onChange,
	className = "",
	scrollerClassName = "",
	listClassName = "gap-6",
	tabClassName = "py-2 px-4 text-sm",
	activeClassName = "text-jmt-main",
	inactiveClassName = "text-jmt-neutral-500 hover:text-jmt-main",
	badgeClassName = "",
	scroll_active_into_view = true,
	endContent = null,
	...rest
}) => {
	const scrollerRef = useRef(null);
	const tabRefs = useRef({});

	useEffect(() => {
		if (!scroll_active_into_view) return;

		const scroller = scrollerRef.current;
		if (!scroller) return;

		const center_active = (behavior = "smooth") => {
			const node = tabRefs.current[value];
			if (!node) return;

			const overflow = scroller.scrollWidth - scroller.clientWidth;
			if (overflow <= 1) return;

			const scroller_rect = scroller.getBoundingClientRect();
			const node_rect = node.getBoundingClientRect();

			const delta =
				node_rect.left + node_rect.width / 2 -
				(scroller_rect.left + scroller_rect.width / 2);

			if (Math.abs(delta) > 1) {
				scroller.scrollBy({ left: delta, behavior });
			}
		};

		center_active();

		if (typeof ResizeObserver === "undefined") return;

		let frame = 0;
		const observer = new ResizeObserver(() => {
			cancelAnimationFrame(frame);
			frame = requestAnimationFrame(() => center_active("auto"));
		});

		observer.observe(scroller);
		if (scroller.firstElementChild) observer.observe(scroller.firstElementChild);

		return () => {
			cancelAnimationFrame(frame);
			observer.disconnect();
		};
	}, [value, items?.length, scroll_active_into_view]);

	if (!items?.length) return null;

	// دکمه‌های تب role="tab" دارند و Lighthouse (aria-required-parent) والد role="tablist" می‌خواهد.
	// اما tablist فقط باید تب داشته باشد: با تب‌های لینکی (href) یا endContent نقش داده نمی‌شود.
	const is_tablist = !endContent && items.every((item) => !item?.href);

	return (
		<div className={`w-full ${className}`} {...rest}>
			<div
				ref={scrollerRef}
				className={`w-full overflow-x-auto hide-scrollbar ${scrollerClassName}`}>

				<div
					role={is_tablist ? "tablist" : undefined}
					className={`relative flex items-center w-max min-w-full ${listClassName}`}>

					<div aria-hidden="true" className="absolute inset-x-0 bottom-0 h-1 bg-jmt-neutral-200 rounded-full pointer-events-none" />

					{items.map((item) => {
						const is_active = item?.id === value;

						const shared_class = `relative shrink-0 flex items-center justify-center gap-1 font-bold whitespace-nowrap
							transition-all duration-200 cursor-pointer
							after:content-[''] after:absolute after:inset-x-0 after:bottom-0 after:h-1
							after:rounded-full after:transition-all after:z-10
							${is_active ? `${activeClassName} after:bg-jmt-primary-800` : `${inactiveClassName} after:bg-transparent`}
							${item?.disabled ? "opacity-50 pointer-events-none" : ""}
							${tabClassName}`;

						const content = (
							<>
								<span>{item?.label}</span>
								{item?.badge != null && item?.badge !== false && (
									<span className={badgeClassName}>{item.badge}</span>
								)}
							</>
						);

						if (item?.href) {
							return (
								<Link
									key={item.id}
									ref={(el) => (tabRefs.current[item.id] = el)}
									href={item.href}
									scroll={false}
									aria-current={is_active ? "page" : undefined}
									onClick={() => onChange?.(item.id)}
									className={shared_class}>
									{content}
								</Link>
							);
						}

						return (
							<button
								key={item.id}
								type="button"
								ref={(el) => (tabRefs.current[item.id] = el)}
								onClick={() => onChange?.(item.id)}
								aria-selected={is_active}
								role="tab"
								className={shared_class}>
								{content}
							</button>
						);
					})}

					{endContent ? (
						<div className="shrink-0 ms-auto pb-1">{endContent}</div>
					) : null}
				</div>
			</div>
		</div>
	);
};

export default Tabs;
